import { format } from "date-fns";
import { FC, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";

import { ERoutes } from "../../constants/routes";
import DailyLayout from "../../layout/DailyLayout/DailyLayout";
import {
  selectActiveBrandsState,
  selectSelectedBrandState,
} from "../../store/selectors/brands";
import HeaderDateSelector from "../../components/HeaderDateSelector/HeaderDateSelector";
import {
  selectLastUpdateTimeState,
  selectDailyAverageBrandScoresByDateState,
} from "../../store/selectors/scores";
import { setViewBrandId } from "../../store/brands/slice";
import CardDailyPromptWrapper from "../../components/CardDailyPromptWrapper/CardDailyPromptWrapper";
import {
  clearScoreByPrompts,
  fetchBrandByPromptsSeries,
  fetchBrandSeries,
  resetDailyAveragePrompt,
} from "../../store/scores/slice";
import { toPositiveInt } from "../../utils/toPositiveInt";

import styles from "./PromptsDaily.module.css";



const PromptsDailyView: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, date } = useParams();
  const selectedBrandId = toPositiveInt(id);
  const safeBrandId = selectedBrandId ?? -1;
  const brands = useSelector(selectActiveBrandsState);
  const selectedBrand = useSelector(selectSelectedBrandState);
  const lastUpdateTime = useSelector(selectLastUpdateTimeState(safeBrandId));
  const score = useSelector(selectDailyAverageBrandScoresByDateState(safeBrandId, date));

  useEffect(() => {
    if (!selectedBrandId) return;
    dispatch(fetchBrandByPromptsSeries({ brand_id: selectedBrandId, days: 16 }));
    dispatch(fetchBrandSeries({ days: 16 }));
  }, [dispatch, selectedBrandId]);

  // Sync viewBrandId with URL so BrandWithCompetitorsSelect highlights the right option
  // and lastUpdateTime / score are fetched for the correct brand.
  useEffect(() => {
    if (!selectedBrandId) return;
    if (selectedBrand?.id === selectedBrandId) return;
    if (brands?.length && !brands.find((b) => b.id === selectedBrandId)) return;
    dispatch(setViewBrandId(selectedBrandId));
  }, [brands, dispatch, selectedBrand?.id, selectedBrandId]);

  const handleChangeBrand = useCallback((brandId: number) => {
    if (!date) return;
    dispatch(clearScoreByPrompts());
    dispatch(resetDailyAveragePrompt());
    navigate(
      ERoutes.PromptsDayly.replace(":id", String(brandId)).replace(":date", date),
      { replace: true }
    );
  }, [date, dispatch, navigate]);

  const handleChangeDate = useCallback((value: number) => {
    const formattedDate = format(value, "yyyy-MM-dd");

    if (selectedBrandId) {
      dispatch(clearScoreByPrompts());
      navigate(
        ERoutes.PromptsDayly
          .replace(":id", String(selectedBrandId))
          .replace(":date", formattedDate)
      );
    }
  }, [dispatch, navigate, selectedBrandId]);



  return (
    <DailyLayout
      linkBack={ERoutes.PromptsHome}
      title="Prompts Daily View"
      className={styles['prompts-daily__container']}
    >

      <HeaderDateSelector
        selected={date}
        lastUpdateTime={lastUpdateTime}
        score={score}
        className={styles['prompts-daily__header-date']}
        onBrandChange={handleChangeBrand}
        onChange={handleChangeDate}
      />

      <div className={styles['prompts-daily__list']}>
        <CardDailyPromptWrapper />
      </div>

    </DailyLayout>
  );
};

export default PromptsDailyView;
